import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Plus, Star, Trash2, Check } from 'lucide-react';
import { OptimizedLazyImage } from '@/components/ui/OptimizedLazyImage';
import { usePhotoUploadQueue } from '@/hooks/usePhotoUploadQueue';
import { NativeCropModal } from '@/components/crop/NativeCropModal';
import { useNativeCrop } from '@/hooks/useNativeCrop';
import { useAuth } from '@/hooks/useAuth';
import PhotoUploadProgress from '@/components/onboarding/PhotoUploadProgress';

interface PhotoManagementModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  photos: string[];
  mainPhoto: string;
  onPhotosChange: (photos: string[]) => void;
  onMainPhotoChange: (photo: string) => void;
  username: string;
}

export const PhotoManagementModal: React.FC<PhotoManagementModalProps> = ({
  isOpen,
  onOpenChange,
  photos,
  mainPhoto,
  onPhotosChange,
  onMainPhotoChange,
  username
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  
  const {
    enqueueCropping,
    onCropComplete: queueCropComplete,
    skipCurrentCrop,
    getCurrentCropFile,
    croppingQueue,
    currentCroppingIndex,
    currentUploadIndex,
    totalCropping,
    totalUploading,
    failedUploads,
    hasActiveWork,
    error,
    retryFailedUpload,
    discardFailedUpload,
  } = usePhotoUploadQueue({
    userId: user?.id || '',
    existingPhotos: photos,
    onPhotosChange: (newPhotos) => {
      onPhotosChange(newPhotos);
      if (!mainPhoto && newPhotos.length > 0) {
        onMainPhotoChange(newPhotos[0]);
      }
    },
    maxPhotos: 6,
  });

  const {
    isOpen: isCropModalOpen,
    imageSrc: cropImageSrc,
    openCrop,
    closeCrop: closeCropModal,
    handleCropComplete: onCropComplete,
  } = useNativeCrop({
    onCropComplete: queueCropComplete,
  });

  // Auto-open crop modal when there's a file in cropping queue (same as onboarding)
  useEffect(() => {
    const currentFile = getCurrentCropFile();
    if (currentFile && !isCropModalOpen) {
      openCrop(currentFile);
    }
  }, [croppingQueue.length, getCurrentCropFile, openCrop, isCropModalOpen]);

  // Handle cancel: skip current photo and close modal
  const handleCancelCrop = useCallback(() => {
    skipCurrentCrop();
    closeCropModal();
  }, [skipCurrentCrop, closeCropModal]);

  const allPhotos = React.useMemo(() => {
    const photoArray: string[] = [];
    if (mainPhoto && !photos.includes(mainPhoto)) {
      photoArray.push(mainPhoto);
    }
    photos.forEach(photo => {
      if (!photoArray.includes(photo)) {
        photoArray.push(photo);
      }
    });
    return photoArray;
  }, [mainPhoto, photos]);

  const handleRemovePhoto = (photoToRemove: string) => {
    const updatedPhotos = allPhotos.filter(photo => photo !== photoToRemove);
    onPhotosChange(updatedPhotos);
    
    if (photoToRemove === mainPhoto && updatedPhotos.length > 0) {
      onMainPhotoChange(updatedPhotos[0]);
    }
  };

  const handleSetMainPhoto = (photo: string) => {
    onMainPhotoChange(photo);
    const updatedPhotos = [photo, ...allPhotos.filter(p => p !== photo)];
    onPhotosChange(updatedPhotos);
  };

  const isImageFile = (file: File): boolean => {
    const validMimeTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validMimeTypes.includes(file.type.toLowerCase())) {
      return false;
    }
    
    const validExtensions = ['.jpg', '.jpeg', '.png', '.webp'];
    const fileName = file.name.toLowerCase();
    return validExtensions.some(ext => fileName.endsWith(ext));
  };

  const handleAddPhoto = useCallback(() => {
    if (isButtonDisabled || hasActiveWork) return;
    
    // Clear any lingering modal state
    if (isCropModalOpen) {
      closeCropModal();
    }
    
    // Debounce: disable button for 1 second
    setIsButtonDisabled(true);
    setTimeout(() => setIsButtonDisabled(false), 1000);
    
    fileInputRef.current?.click();
  }, [isButtonDisabled, hasActiveWork, isCropModalOpen, closeCropModal]);
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const allFiles = Array.from(e.target.files);
    const imageFiles = allFiles.filter(isImageFile);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (imageFiles.length === 0) return;

    // Add all files to cropping queue (with duplicate prevention)
    enqueueCropping(imageFiles);
  };

  const isDisabled = isButtonDisabled || hasActiveWork;
  const canComplete = allPhotos.length >= 3;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-black/95 border-white/20 flex flex-col">
        <DialogDescription className="sr-only">
          Gerencie suas fotos de perfil, defina a foto principal e adicione novas imagens.
        </DialogDescription>
        <DialogHeader>
          <DialogTitle className="text-white text-xl">Gerenciar Fotos</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Progress display with failed uploads */}
          <PhotoUploadProgress
            croppingProgress={{ current: currentCroppingIndex, total: totalCropping }}
            uploadProgress={{ current: currentUploadIndex, total: totalUploading }}
            failedUploads={failedUploads}
            onRetryUpload={retryFailedUpload}
            onDiscardUpload={discardFailedUpload}
          />

          {/* Compact upload area */}
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-3">
              <div className="border border-dashed border-white/20 rounded-lg p-4 flex items-center justify-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isDisabled}
                />
                
                <span className="text-white/60 text-sm">Arraste fotos ou</span>
                
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:from-[#D4507B] hover:to-[#AF4774] text-white text-sm px-3 py-1.5 h-auto"
                  onClick={handleAddPhoto}
                  disabled={isDisabled}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {hasActiveWork ? 'Processando...' : 'Adicionar'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {allPhotos.length > 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-3">Suas Fotos ({allPhotos.length})</h3>
                <p className="text-white/60 text-sm mb-3">
                  Clique em "Principal" para definir a foto de capa.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {allPhotos.map((photo, index) => (
                    <div key={photo} className="relative group">
                      <div className="relative aspect-square rounded-lg overflow-hidden bg-white/10">
                        <OptimizedLazyImage
                          src={photo}
                          alt={`Foto ${index + 1}`}
                          className="w-full h-full object-cover"
                          isMainPhoto={photo === mainPhoto}
                          showRemoveButton={false}
                          showSetMainButton={false}
                          disableBlur={true}
                        />
                      </div>
                      
                      {photo === mainPhoto && (
                        <div className="absolute top-2 left-2 z-10">
                          <div className="flex items-center gap-1 bg-luxury-gold text-black px-2 py-1 rounded text-xs font-medium shadow-lg">
                            <Star className="h-3 w-3 fill-current" />
                            Principal
                          </div>
                        </div>
                      )}
                      
                      <button
                        onClick={() => handleRemovePhoto(photo)}
                        className="absolute top-2 right-2 z-10 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors duration-200 shadow-lg"
                        aria-label="Remover foto"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      
                      {photo !== mainPhoto && (
                        <button
                          onClick={() => handleSetMainPhoto(photo)}
                          className="absolute bottom-2 left-2 z-10 bg-white hover:bg-gray-100 text-black px-3 py-1.5 rounded text-sm font-medium transition-colors duration-200 shadow-lg"
                        >
                          Principal
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {allPhotos.length === 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6 text-center">
                <Avatar className="h-20 w-20 mx-auto mb-3">
                  <AvatarImage src="" />
                  <AvatarFallback className="bg-luxury-gold/20 text-luxury-gold text-xl">
                    {username?.[0]?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <p className="text-white/80 mb-1">Você ainda não tem fotos</p>
                <p className="text-white/60 text-sm">Adicione algumas fotos para deixar seu perfil mais atrativo</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sticky footer with Concluir button - only shows with ≥3 photos */}
        {canComplete && (
          <div className="sticky bottom-0 pt-4 mt-4 border-t border-white/10 bg-black/95">
            <Button
              className="w-full bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:from-[#D4507B] hover:to-[#AF4774] text-white font-medium py-3 h-auto"
              onClick={() => onOpenChange(false)}
              disabled={hasActiveWork}
            >
              <Check className="h-5 w-5 mr-2" />
              Concluir ({allPhotos.length} fotos)
            </Button>
          </div>
        )}

        <NativeCropModal
          isOpen={isCropModalOpen}
          imageSrc={cropImageSrc}
          onCropComplete={onCropComplete}
          onClose={handleCancelCrop}
        />
      </DialogContent>
    </Dialog>
  );
};